import React, {
  forwardRef,
  InputHTMLAttributes,
  ReactNode,
  useCallback,
} from 'react';

export type CheckboxSize = 'sm' | 'md';

interface OwnProps {
  /**
   * Visible label rendered to the right of the box. Pass plain text or a
   * React node (used by InlineEditor to inject `<span>` markup).
   */
  label?: ReactNode;
  /**
   * Optional secondary line beneath the label — for hints / micro-copy.
   */
  description?: ReactNode;
  /**
   * `sm` for inline-table affordances, `md` for filter rows + form rows.
   */
  size?: CheckboxSize;
  /**
   * When true, renders no visible chrome around the row (hover wash, gap).
   * Use inside dense tables where the cell already provides the affordance.
   */
  bare?: boolean;
  /**
   * Indeterminate state — exclusive with `checked`. Sets the native
   * `indeterminate` flag on the input via ref.
   */
  indeterminate?: boolean;
  className?: string;
}

export type CheckboxProps = OwnProps &
  Omit<InputHTMLAttributes<HTMLInputElement>, keyof OwnProps | 'type' | 'size'>;

/**
 * Canonical checkbox primitive.
 *
 * Visual anatomy:
 *   ┌──────────────────────────────────────────┐
 *   │ [ ✓ ]   Label text                        │
 *   │         Optional description              │
 *   └──────────────────────────────────────────┘
 *
 * - 18×18 box (sm = 16×16) with 6px corner radius — same family as buttons
 * - Idle: 1.5px slate-300 border, white fill
 * - Hover: border darkens to purple-400
 * - Checked: filled with brand purple, white tick (CSS-mask SVG)
 * - Focus: 2px purple ring at 2px offset (matches button focus-visible)
 * - Disabled: slate-200 fill, slate-400 border, label dims to 0.5
 *
 * Replaces every raw `<input type="checkbox">` in the codebase. Always
 * use this primitive — never write a bespoke checkbox.
 */
const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  props,
  forwardedRef,
) {
  const {
    label,
    description,
    size = 'md',
    bare = false,
    indeterminate = false,
    className,
    id,
    disabled,
    ...rest
  } = props;

  // Composite ref so consumers can still grab the input AND we can set
  // `indeterminate` (a property, not an attribute, so React can't bind it).
  const setRef = useCallback(
    (node: HTMLInputElement | null) => {
      if (node) {
        node.indeterminate = indeterminate;
      }
      if (typeof forwardedRef === 'function') {
        forwardedRef(node);
      } else if (forwardedRef) {
        (forwardedRef as React.MutableRefObject<HTMLInputElement | null>).current = node;
      }
    },
    [forwardedRef, indeterminate],
  );

  const wrapperClass = [
    'kk-checkbox',
    `kk-checkbox--${size}`,
    bare ? 'kk-checkbox--bare' : null,
    disabled ? 'kk-checkbox--disabled' : null,
    indeterminate ? 'kk-checkbox--indeterminate' : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const inputId = id;

  // The native input lives behind a custom-painted box for full visual
  // control while keeping native a11y semantics (focus, keyboard, SR
  // announcements). The label wraps both so click anywhere flips the box.
  const Wrapper: 'label' | 'div' = label || description ? 'label' : 'div';

  return (
    <Wrapper className={wrapperClass} htmlFor={Wrapper === 'label' ? inputId : undefined}>
      <span className="kk-checkbox__control">
        <input
          ref={setRef}
          id={inputId}
          type="checkbox"
          className="kk-checkbox__input"
          disabled={disabled}
          {...rest}
        />
        <span className="kk-checkbox__box" aria-hidden="true">
          <svg
            className="kk-checkbox__tick"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3.5 8.25L6.5 11.25L12.5 5.25"
              stroke="currentColor"
              strokeWidth="2.25"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <svg
            className="kk-checkbox__dash"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3.5 8H12.5"
              stroke="currentColor"
              strokeWidth="2.25"
              strokeLinecap="round"
            />
          </svg>
        </span>
      </span>
      {(label || description) && (
        <span className="kk-checkbox__meta">
          {label && <span className="kk-checkbox__label">{label}</span>}
          {description && (
            <span className="kk-checkbox__description">{description}</span>
          )}
        </span>
      )}
    </Wrapper>
  );
});

export default Checkbox;
